export interface AlertOptions {
  title?: string
  message?: string
  type?: 'success' | 'error' | 'warning' | 'info' | 'question'
  confirmText?: string
  cancelText?: string
  showCancel?: boolean
  icon?: string
  color?:
    | 'primary'
    | 'secondary'
    | 'error'
    | 'success'
    | 'info'
    | 'warning'
    | 'neutral'
  size?: 'sm' | 'md' | 'lg' | 'xl'
  timer?: number
  allowOutsideClick?: boolean
  allowEscapeKey?: boolean
  persistent?: boolean
  loading?: boolean
  html?: boolean
  showProgressBar?: boolean // Nova opcija
  position?: 'center' | 'top' | 'bottom' // Nova opcija
}

export interface AlertState {
  isOpen: boolean
  title: string
  message: string
  type: 'success' | 'error' | 'warning' | 'info' | 'question'
  confirmText: string
  cancelText: string
  showCancel: boolean
  icon: string
  color:
    | 'primary'
    | 'secondary'
    | 'error'
    | 'success'
    | 'info'
    | 'warning'
    | 'neutral'
  size: 'sm' | 'md' | 'lg' | 'xl'
  timer?: number
  allowOutsideClick: boolean
  allowEscapeKey: boolean
  persistent: boolean
  loading: boolean
  html: boolean
  showProgressBar: boolean
  position: 'center' | 'top' | 'bottom'
  progress: number // Za progress bar
}

const alertState = reactive<AlertState>({
  isOpen: false,
  title: '',
  message: '',
  type: 'info',
  confirmText: 'OK',
  cancelText: 'Otkaži',
  showCancel: false,
  icon: '',
  color: 'primary',
  size: 'md',
  allowOutsideClick: true,
  allowEscapeKey: true,
  persistent: false,
  loading: false,
  html: false,
  showProgressBar: false,
  position: 'center',
  progress: 100,
})

let resolvePromise: ((value: boolean) => void) | null = null
let timerRef: ReturnType<typeof setTimeout> | null = null
let progressInterval: ReturnType<typeof setInterval> | null = null

const typeConfig = {
  success: {
    icon: 'i-lucide-circle-check',
    color: 'success' as const,
  },
  error: {
    icon: 'i-lucide-circle-x',
    color: 'error' as const,
  },
  warning: {
    icon: 'i-lucide-triangle-alert',
    color: 'warning' as const,
  },
  info: {
    icon: 'i-lucide-info',
    color: 'info' as const,
  },
  question: {
    icon: 'i-lucide-circle-help',
    color: 'secondary' as const,
  },
}

export const useAlert = () => {
  const showAlert = (options: AlertOptions = {}): Promise<boolean> => {
    return new Promise((resolve) => {
      resolvePromise = resolve

      // Očisti postojeće timere
      if (timerRef) {
        clearTimeout(timerRef)
        timerRef = null
      }
      if (progressInterval) {
        clearInterval(progressInterval)
        progressInterval = null
      }

      const config = typeConfig[options.type || 'info']

      // Postavi state
      alertState.isOpen = true
      alertState.title = options.title || ''
      alertState.message = options.message || ''
      alertState.type = options.type || 'info'
      alertState.confirmText = options.confirmText || 'Potvrdi'
      alertState.cancelText = options.cancelText || 'Otkaži'
      alertState.showCancel = options.showCancel ?? false
      alertState.icon = options.icon || config.icon
      alertState.color = options.color || config.color
      alertState.size = options.size || 'md'
      alertState.allowOutsideClick = options.allowOutsideClick ?? true
      alertState.allowEscapeKey = options.allowEscapeKey ?? true
      alertState.persistent = options.persistent ?? false
      alertState.loading = options.loading ?? false
      alertState.html = options.html ?? false
      alertState.showProgressBar = options.showProgressBar ?? false
      alertState.position = options.position || 'center'
      alertState.progress = 100

      // Timer sa progress barom
      if (options.timer && options.timer > 0) {
        const startTime = Date.now()
        const duration = options.timer

        if (alertState.showProgressBar) {
          progressInterval = setInterval(() => {
            const elapsed = Date.now() - startTime
            const remaining = Math.max(0, duration - elapsed)
            alertState.progress = (remaining / duration) * 100

            if (remaining <= 0) {
              clearInterval(progressInterval!)
              progressInterval = null
            }
          }, 16) // ~60fps
        }

        timerRef = setTimeout(() => {
          closeAlert(true)
        }, duration)
      }
    })
  }

  const closeAlert = (result: boolean = false) => {
    alertState.isOpen = false
    alertState.loading = false
    alertState.progress = 100

    // Očisti timere
    if (timerRef) {
      clearTimeout(timerRef)
      timerRef = null
    }
    if (progressInterval) {
      clearInterval(progressInterval)
      progressInterval = null
    }

    if (resolvePromise) {
      resolvePromise(result)
      resolvePromise = null
    }
  }

  const confirmAlert = () => {
    closeAlert(true)
  }

  const cancelAlert = () => {
    closeAlert(false)
  }

  // Shortcut metode
  const showSuccess = (
    message: string,
    title?: string,
    options?: Omit<AlertOptions, 'type' | 'message' | 'title'>
  ) => {
    return showAlert({ ...options, type: 'success', message, title })
  }

  const showError = (
    message: string,
    title?: string,
    options?: Omit<AlertOptions, 'type' | 'message' | 'title'>
  ) => {
    return showAlert({ ...options, type: 'error', message, title })
  }

  const showWarning = (
    message: string,
    title?: string,
    options?: Omit<AlertOptions, 'type' | 'message' | 'title'>
  ) => {
    return showAlert({ ...options, type: 'warning', message, title })
  }

  const showInfo = (
    message: string,
    title?: string,
    options?: Omit<AlertOptions, 'type' | 'message' | 'title'>
  ) => {
    return showAlert({ ...options, type: 'info', message, title })
  }

  const showQuestion = (
    message: string,
    title?: string,
    options?: Omit<AlertOptions, 'type' | 'message' | 'title'>
  ) => {
    return showAlert({
      ...options,
      type: 'question',
      message,
      title,
      showCancel: true,
    })
  }

  const showConfirm = (
    message: string,
    title?: string,
    options?: Omit<AlertOptions, 'type' | 'message' | 'title' | 'showCancel'>
  ) => {
    return showAlert({
      ...options,
      type: 'question',
      message,
      title,
      showCancel: true,
    })
  }

  const showLoading = (message: string, title?: string) => {
    return showAlert({
      type: 'info',
      message,
      title,
      loading: true,
      allowOutsideClick: false,
      allowEscapeKey: false,
      persistent: true,
      showCancel: false,
    })
  }

  const showToast = (
    title: string,
    message: string,
    type: AlertOptions['type'] = 'info',
    timer: number = 3000
  ) => {
    const toast = useToast()
    toast.add({
      title: title,
      description: message,
      icon: typeConfig[type]?.icon || 'i-heroicons-information-circle',
      color: typeConfig[type]?.color || 'info',
      duration: timer,
    })
  }

  // Nova metoda za timer sa progress barom
  const showTimedAlert = (
    message: string,
    title: string,
    timer: number = 5000,
    type: AlertOptions['type'] = 'info'
  ) => {
    return showAlert({
      type,
      message,
      title,
      timer,
      showProgressBar: true,
      allowOutsideClick: false,
    })
  }

  return {
    // State
    alertState: readonly(alertState),

    // Osnovne metode
    showAlert,
    closeAlert,
    confirmAlert,
    cancelAlert,

    // Shortcut metode
    showSuccess,
    showError,
    showWarning,
    showInfo,
    showQuestion,
    showConfirm,
    showLoading,
    showToast,
    showTimedAlert,
  }
}
